import React from "react";
import {
  AvatarGroup,
  Badge,
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  CalendarToday as CalendarIcon,
  ChatBubbleOutline as CommentIcon,
  AttachFile as AttachmentIcon,
  AccountTree as FlownodeIcon,
  ListAltRounded as FieldIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { Task } from "model/activitiesModel";
import { getTranslation } from "common";
import { getParticipants, getInitials, getDisplayName } from "common/utilities";
import { useTranslations } from "hooks";
import AvatarRem from "components/AvatarRem";
import TaskQuickSwapButtons from "components/TaskQuickSwapButtons";

interface MobileTaskCardCompactProps {
  task: Task;
  onClick: () => void;
  onNext?: (task: Task) => void | Promise<void>;
  onBack?: (task: Task) => void | Promise<void>;
}

/**
 * Ultra-compact mobile task card for the landscape split layout.
 *
 * Same visual style as the portrait `MobileTaskCard` (same
 * background, padding, border-radius, font sizes and avatar
 * sizing) — the only thing that changes versus the portrait is the
 * missing date column (the column header above already provides the
 * date context in landscape split layout).
 *
 *   row 1: BUG FIX (left), priority + kebab (right)
 *   row 2: activity name (flownodename)
 *   row 3: description (process name) — bold, LEFT-aligned
 *   row 4: small flownode icon + processmodelname (model name)
 *   row 5 (footer): icons + avatars + TaskQuickSwapButtons
 */
const MobileTaskCardCompact: React.FC<MobileTaskCardCompactProps> = ({
  task,
  onClick,
  onNext,
  onBack,
}) => {
  const theme = useTheme();
  const translations = useTranslations();

  const date = task.created ? dayjs(task.created).format("DD/MM") : "";
  const participants = getParticipants(task.jsonparticipant);

  const priorityColor = theme.palette.primary.main;
  const priorityBorder = `1.5px solid ${priorityColor}`;
  const isDoing = task.state === 1;
  const isBugFix = Boolean((task as Task & { bugfix?: boolean }).bugfix);

  const commentsLabel = getTranslation(translations, "settings.comments.label");
  const fieldsLabel = getTranslation(translations, "generic.fields.caption");
  const attachmentsLabel = getTranslation(translations, "generic.attached_files.caption");
  const detailLabel =
    getTranslation(translations, "generic.detail.caption") || "Dettagli";

  const icons = [
    { title: commentsLabel, IconComponent: CommentIcon, count: task.countcomments ?? 0 },
    { title: fieldsLabel, IconComponent: FieldIcon, count: task.countuserdatafields ?? 0 },
    { title: attachmentsLabel, IconComponent: AttachmentIcon, count: task.countattachments ?? 0 },
  ];

  const activityName = task.flownodename || task.name || "";
  const description = task.description || "";
  const processModelName = task.processmodelname || "";

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      sx={{
        // Same chrome as the portrait `MobileTaskCard` so the two views
        // are visually identical regardless of orientation.
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 1,
        border: "1px solid",
        borderColor: "divider",
        p: 1.5,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        transition: "transform 0.15s, box-shadow 0.15s",
        "&:active": { transform: "scale(0.99)" },
        "&:hover": { boxShadow: 3 },
      }}
    >
      {/* Top row: BUG FIX (left) + priority + kebab (right). Same as portrait,
          just without the date column (landscape shows it in the column header). */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.75}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          {isBugFix && (
            <Chip
              label="BUG FIX"
              size="small"
              sx={{
                height: 16,
                fontSize: 8,
                fontWeight: 800,
                color: "primary.main",
                border: `1px solid ${theme.palette.primary.main}`,
                bgcolor: "transparent",
                borderRadius: 0.5,
                "& .MuiChip-label": { px: 0.5 },
              }}
            />
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          {date && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "text.secondary" }}>
              <CalendarIcon sx={{ fontSize: 12 }} />
              <Typography variant="caption" sx={{ fontSize: 10 }}>
                {date}
              </Typography>
            </Stack>
          )}
          {task.priority_name && (
            <Chip
              size="small"
              label={task.priority_name.length > 6 ? `${task.priority_name.substring(0, 6)}…` : task.priority_name}
              sx={{
                height: 18,
                fontSize: 9,
                fontWeight: 700,
                border: priorityBorder,
                bgcolor: "transparent",
                color: priorityColor,
                borderRadius: 0.75,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          )}
          <Tooltip title={detailLabel}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
              sx={{
                width: 24,
                height: 24,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 0.75,
              }}
            >
              <MoreHorizIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Activity name (top, replaces the old T-xxx code). */}
      {activityName && (
        <Typography
          variant="caption"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: "text.secondary",
            letterSpacing: 0.4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {activityName}
        </Typography>
      )}

      {/* Description (process name) — bold, LEFT-aligned, 2 lines max. */}
      {description && (
        <Typography
          variant="body2"
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: "text.primary",
            lineHeight: 1.35,
            textAlign: "left",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {description}
        </Typography>
      )}

      {/* Process model: small flownode icon + name — same as portrait. */}
      {processModelName && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <FlownodeIcon sx={{ fontSize: 12, color: "primary.main", flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              color: "text.secondary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {processModelName}
          </Typography>
        </Stack>
      )}

      {/* Footer row: icons + avatars + quick swap. Same sizing as portrait. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.75}
        sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Stack direction="row" alignItems="center" spacing={0.25}>
          {icons.map(({ title, IconComponent, count }) => (
            <Tooltip key={title} title={title} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
                sx={{ p: 0.5 }}
              >
                <Badge
                  badgeContent={count}
                  color="primary"
                  sx={{
                    "& .MuiBadge-badge": { fontSize: 8, height: 12, minWidth: 12, px: 0.25 },
                  }}
                >
                  <IconComponent sx={{ fontSize: 14, color: "text.secondary" }} />
                </Badge>
              </IconButton>
            </Tooltip>
          ))}
        </Stack>

        {participants.length > 0 && (
          <Tooltip
            arrow
            title={participants.map((p) => getDisplayName(p)).join(", ")}
            sx={{ mr: "auto", ml: 0.5 }}
          >
            <AvatarGroup
              max={3}
              sx={{
                "& .MuiAvatar-root": {
                  width: 20,
                  height: 20,
                  fontSize: "0.65rem",
                  border: "1px solid",
                  borderColor: "background.paper",
                },
              }}
            >
              {participants.slice(0, 3).map((p) => {
                let iconUrl = `/users/${p.idparticipant}/picture`;
                if (p.participanttype === 1) iconUrl = `/downloads/groups/${p.idparticipant}`;
                else if (p.participanttype === 2) iconUrl = `/downloads/roles/${p.idparticipant}`;
                return (
                  <AvatarRem key={p.idparticipant} icon={iconUrl}>
                    {getInitials(p)}
                  </AvatarRem>
                );
              })}
            </AvatarGroup>
          </Tooltip>
        )}

        <Box onClick={(e) => e.stopPropagation()}>
          <TaskQuickSwapButtons
            task={task}
            onBack={isDoing && onBack ? onBack : undefined}
            onNext={onNext}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default MobileTaskCardCompact;
